import React, { useState, useEffect, useCallback } from "react";
import { Copy, Loader2, ShoppingCart, Printer, Package, AlertTriangle } from "lucide-react";
import { SectionHeading, StepInstructions, Chip } from "@/components/wizard/ui";
import PrintHeader from "@/components/PrintHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const REGIONS = [
  { value: "UK", label: "UK" },
  { value: "US", label: "US" },
  { value: "EU", label: "EU" },
];

// Full weekday names, indexed 0=Mon..6=Sun to match backend's day
// ordering. Used for the compact "days-used" chip on each shopping item so
// athletes see at a glance which days each ingredient serves without a
// noisy 7-badge strip.
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Compose a compact days-used string: "Mon · Wed" for 1–3 days, "5×/week"
// for anything higher, so the shopping list stays legible.
function daysUsedLabel(days) {
  if (!Array.isArray(days) || days.length === 0) return "";
  if (days.length >= 4) return `${days.length}× / week`;
  return days.slice().sort((a, b) => a - b).map((d) => DAY_LABELS[d]).filter(Boolean).join(" · ");
}

// Backend used to return the shopping list as a plain array; new shape is
// `{groups: [...], region}`. Support both so cached older plans still render.
function readGroups(sh) {
  if (!sh) return [];
  return Array.isArray(sh) ? sh : (sh.groups || []);
}

export default function Step7Shopping({ plan, setPlan, patch }) {
  const { generated, selections, name, profile } = plan;
  const [shopping, setShopping] = useState(generated?.shopping || null);
  const [loading, setLoading] = useState(false);
  const householdSize = Math.max(1, parseInt(profile?.householdSize, 10) || 1);
  const isFamily = profile?.cookingStyle === "family";
  const showFamily = isFamily && householdSize > 1;
  const [familyMode, setFamilyMode] = useState(false);
  const mult = (familyMode && showFamily) ? householdSize : 1;
  const scaleQty = (q) => Math.round(q * mult * 10) / 10;
  const region = profile?.shoppingRegion || "UK";
  const groups = readGroups(shopping);

  const refresh = useCallback(async () => {
    if (!generated?.dayPathways) return;
    setLoading(true);
    try {
      const sel = {};
      Object.keys(selections).forEach((k) => { sel[String(k)] = selections[k]; });
      const { data } = await api.post("/engine/shopping", { dayPathways: generated.dayPathways, selections: sel, region });
      setShopping(data.shopping);
      setPlan((p) => ({ ...p, generated: { ...p.generated, shopping: data.shopping } }));
    } catch (e) { console.error("Shopping list refresh failed:", e?.message || e); } finally { setLoading(false); }
  }, [generated, selections, setPlan, region]);

  const pathSig = JSON.stringify(
    (generated?.dayPathways || []).map((d) => Object.values(d.pathways || {}).map((p) => (p.meals || []).map((m) => `${m.id}:${m.scale || 1}`)))
  );
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [JSON.stringify(selections), pathSig, region]);

  const copyList = () => {
    if (!groups.length) return;
    const header = mult > 1 ? `Shopping list for ${householdSize} people\n\n` : "";
    const text = header + groups.map((g) => `${g.label.toUpperCase()}\n` +
      g.items.map((it) => {
        const pk = it.pack;
        const suffix = pk ? `  →  buy ${pk.packs} × ${pk.label}` : "";
        return `  - ${it.name}: ${scaleQty(it.qty)}${it.unit}${suffix}`;
      }).join("\n")).join("\n\n");
    navigator.clipboard.writeText(text).catch(() => {});
    toast.success("Shopping list copied");
  };

  if (!generated?.dayPathways) {
    return (
      <div>
        <SectionHeading eyebrow="Step 08" title="Shopping List" />
        <div className="border border-dashed border-white/15 p-12 text-center">
          <ShoppingCart className="h-6 w-6 text-zinc-200 mx-auto mb-3" />
          <p className="text-zinc-200">Generate a plan and pick pathways first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="print-area">
      <PrintHeader title="Weekly Shopping List" subtitle={name} />
      <SectionHeading eyebrow="Step 08" title="Shopping List"
        desc="Aggregated across every selected pathway for the full week, grouped by aisle and rounded up to real grocery packs." />

      <StepInstructions>
        Your full week's ingredients, grouped by aisle and rounded up to the retail pack sizes you'll actually find in the shop. <span className="text-[#E2E30A]">Copy</span> it to your phone or <span className="text-[#E2E30A]">Print / PDF</span> it — then head to the shops. It updates automatically if you change a pathway.
      </StepInstructions>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4 no-print">
        <div className="flex items-center gap-2" data-testid="shopping-region-picker">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Pack sizes</span>
          <div className="flex gap-1.5">
            {REGIONS.map((r) => (
              <Chip key={r.value} testId={`shopping-region-${r.value}`} active={region === r.value}
                onClick={() => (patch ? patch("profile", { shoppingRegion: r.value })
                                     : setPlan((p) => ({ ...p, profile: { ...p.profile, shoppingRegion: r.value } })))}>
                {r.label}
              </Chip>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="copy-shopping-btn" onClick={copyList} variant="outline"
            className="rounded-none border-white/10 bg-transparent hover:bg-white/5 text-white uppercase text-xs tracking-wider">
            <Copy className="h-3.5 w-3.5 mr-2" /> Copy list
          </Button>
          <Button data-testid="print-shopping-btn" onClick={() => window.print()}
            className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase text-xs tracking-wider transition-colors">
            <Printer className="h-3.5 w-3.5 mr-2" /> Print / PDF
          </Button>
        </div>
      </div>

      {showFamily && (
        <div className="flex items-center gap-3 mb-4 no-print border border-white/10 bg-[#141414] px-4 py-3">
          <button type="button" data-testid="family-shopping-toggle" onClick={() => setFamilyMode((v) => !v)}
            className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${familyMode ? "bg-[#E2E30A]" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-black transition-transform ${familyMode ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </button>
          <div>
            <p className="text-sm text-white">Cater for the whole household</p>
            <p className="text-[11px] text-zinc-400">{familyMode ? `Quantities multiplied by ${householdSize} people` : `Toggle to multiply every quantity by ${householdSize} people`}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-200"><Loader2 className="h-4 w-4 animate-spin" /> Aggregating…</div>
      ) : (
        <>
          {groups.some((g) => Array.isArray(g?.items) && g.items.some((it) => it && it.singleUse && it.perishable)) && (
            <div data-testid="shopping-waste-legend"
              className="mb-3 border-l-2 border-[#FF9500] bg-[#FF9500]/10 px-3 py-2 text-[11px] text-[#FFB454] no-print">
              <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wide">
                <AlertTriangle className="h-3.5 w-3.5" /> Waste warning
              </span>
              <span className="ml-2">Items flagged in orange are perishables used on only one day this week — buy loose or the smallest pack.</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shopping-grid">
            {groups.map((g) => {
              if (!g || !Array.isArray(g.items)) return null;
              return (
              <div key={g.category} data-testid={`shopping-cat-${g.category}`} className="border border-white/10 bg-[#141414] print-day">
                <div className="px-4 py-2 border-b border-white/10 bg-[#0A0A0A]">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#E2E30A] font-semibold">{g.label}</span>
                </div>
                <ul className="divide-y divide-white/5">
                  {g.items.map((it) => {
                  if (!it || !it.name) return null;
                  const safeKey = it.name.replace(/\W+/g, '-').toLowerCase();
                  const pk = it.pack;
                  const waste = it.singleUse && it.perishable;
                  return (
                    <li key={it.name} data-testid={`shopping-item-${safeKey}`}
                      className={`px-4 py-2 text-sm ${waste ? "border-l-2 border-[#FF9500]" : ""}`}>
                      <div className="flex justify-between gap-3 items-start">
                        <div className="flex-1 min-w-0">
                          <span className="text-white">{it.name}</span>
                          {(Array.isArray(it.days) && it.days.length > 0) && (
                            <div className="mt-0.5 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider"
                              data-testid={`shopping-item-days-${safeKey}`}>
                              <span className={waste ? "text-[#FFB454]" : "text-zinc-500"}>
                                {daysUsedLabel(it.days)}
                              </span>
                              {waste && (
                                <span className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-wider text-[#FFB454]">
                                  <AlertTriangle className="h-2.5 w-2.5" /> Single use
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="text-zinc-200 font-mono shrink-0">{scaleQty(it.qty)}{it.unit}</span>
                      </div>
                      {pk && (
                        <div className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
                          <Package className="h-3 w-3 text-[#E2E30A] shrink-0" />
                          <span className="text-zinc-300">
                            Buy <span className="text-[#E2E30A] font-semibold">{pk.packs} × {pk.label}</span>
                            {pk.eachNeeded ? <span className="text-zinc-400"> · uses {pk.eachNeeded}</span> : null}
                          </span>
                        </div>
                      )}
                    </li>
                  );
                })}
                </ul>
              </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
